import { CorporationDataType } from '@commenda/types/src/corporation';
import {
  DirectorBase,
  TValidateStakeholdersResponse,
} from '@commenda/types/src/incorporation/types';
import { getCorporationName } from '@commenda/utils';
import { LegacyDocumentType, StakeholderType } from '@prisma/client';
import { ZodError } from 'zod';
import { corporationDetailsSchema } from '../common/corporateStakeholder/CorporationDetailsForm';
import { getDirectorName } from '../Director/common';
import { TShareholderVsSharePercentageMap } from '../EquityInfo/types';
import { directorSchema } from './schema';

const validateDirectorInfo = (
  directors: DirectorBase[],
  selectedDirectorIds: number[]
) => {
  const directorWithError = [];
  const selectedDirectors = directors?.filter((director) =>
    selectedDirectorIds.includes(director.id)
  );

  for (let i = 0; i < selectedDirectors.length; i += 1) {
    const passportFile = selectedDirectors[i]?.directorFiles?.find(
      (file) => file.documentType === LegacyDocumentType.Passport
    );
    const directorSchemaWithoutPassport = directorSchema.omit({
      passportProof: true,
    });

    const validationResult = directorSchemaWithoutPassport.safeParse(
      selectedDirectors[i]
    ) as {
      success: boolean;
      error: any;
    };

    if (!validationResult.success || !passportFile) {
      if (validationResult.error) {
        directorWithError.push({
          name: getDirectorName(selectedDirectors[i]),
          id: selectedDirectors[i]?.id,
        });
      }
    }
  }

  return directorWithError;
};

const validateUKStakeholders = (
  selectedStakeholders: TShareholderVsSharePercentageMap,
  {
    directors,
    corporations,
  }: {
    directors: DirectorBase[];
    corporations: CorporationDataType[];
  }
): TValidateStakeholdersResponse[] => {
  const shareholderWithError: TValidateStakeholdersResponse[] = [];

  Object.keys(selectedStakeholders).forEach((stakeholderId) => {
    const [type, id] = stakeholderId.split('_');

    if (type === StakeholderType.INDIVIDUAL) {
      const director = directors.find((d) => d.id === +id);

      if (!director) return;

      const directorWithError = validateDirectorInfo(directors, [director.id]);

      if (directorWithError.length > 0) {
        shareholderWithError.push({
          name: getDirectorName(director),
          id: stakeholderId,
        });
      }
    }

    if (type === StakeholderType.INSTITUTION) {
      const corporation = corporations.find((c) => c.id === +id);

      if (!corporation) return;

      const corporationBasicInformationValidationResult =
        corporationDetailsSchema.safeParse(corporation) as {
          success: boolean;
          error: ZodError;
        };

      if (!corporationBasicInformationValidationResult.success) {
        if (corporationBasicInformationValidationResult.error) {
          shareholderWithError.push({
            name: getCorporationName(corporation),
            id: stakeholderId,
          });
        }
      }

      const authorizedSignatory = corporation.corporationHasDirector?.find(
        (director) =>
          director.additionalDirectorInformation?.isAuthorizedSignatory
      );

      if (authorizedSignatory) {
        const directorValidationResult = validateDirectorInfo(directors, [
          authorizedSignatory.directorId,
        ]);

        if (directorValidationResult.length > 0) {
          shareholderWithError.push({
            name: getCorporationName(corporation),
            id: stakeholderId,
          });
        }
      } else {
        shareholderWithError.push({
          name: getCorporationName(corporation),
          id: stakeholderId,
        });
      }
    }
  });

  return shareholderWithError;
};

export { validateDirectorInfo, validateUKStakeholders };
